/**
 * Generated by orval 🍺
 * Do not edit manually.
 * Nucleus Admin API
 * Headless, multi-tenant commerce backend for jewelry brands.
 *
 * ## Surfaces
 *
 * | Prefix | Caller | Credential |
 * |---|---|---|
 * | `/platform/v1` | Platform operator | Platform token from **Platform: Auth** |
 * | `/admin/v1` | Staff of one organization | Staff token from **Admin: Auth** |
 * | `/storefront/v1` | A storefront | Store API key from **Admin: API keys** |
 * | `/public/v1` | Anyone (the brand application form) | None; rate limited per IP |
 *
 * Every credential is sent as `Authorization: Bearer <value>`. Use the **Authorize** button once and it applies to every request you try here.
 *
 * ## Conventions
 *
 * - **Ids** are UUID v7 strings. **Timestamps** are ISO 8601 in UTC. **Money** is `{ amount, currency }` with `amount` in integer minor units.
 * - **Lists** paginate by cursor: `?limit=20&cursor=...` in, `{ items, nextCursor }` out, `nextCursor` is `null` on the last page.
 * - **Errors** always use one envelope, `{ error: { code, message, requestId, details?, meta? } }`. Switch on `code`, never on `message`.
 * - **Request ids**: every response carries `x-request-id`. Quote it when reporting a problem.
 * - **Idempotency**: endpoints that create money-related state require an `Idempotency-Key` header; the header is listed on those endpoints.
 * - A record that belongs to another tenant answers **404**, never 403, so existence is not leaked.
 * OpenAPI spec version: 0.1.0
 */
import type {
  AnalyticsDestination,
  AnalyticsDestinationTestResult,
  AnalyticsSummary,
  CreateAnalyticsDestinationRequest,
  DeleteAdminV1StoresStoreIdAnalyticsDestinationsIdPathParameters,
  GetAdminV1StoresStoreIdAnalyticsDestinations200,
  GetAdminV1StoresStoreIdAnalyticsDestinationsPathParameters,
  GetAdminV1StoresStoreIdAnalyticsEvents200,
  GetAdminV1StoresStoreIdAnalyticsEventsParams,
  GetAdminV1StoresStoreIdAnalyticsEventsPathParameters,
  GetAdminV1StoresStoreIdAnalyticsSummaryParams,
  GetAdminV1StoresStoreIdAnalyticsSummaryPathParameters,
  PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody,
  PatchAdminV1StoresStoreIdAnalyticsDestinationsIdPathParameters,
  PostAdminV1StoresStoreIdAnalyticsDestinationsIdTestPathParameters,
  PostAdminV1StoresStoreIdAnalyticsDestinationsPathParameters
} from '../model';

import { customInstance } from '../../../transport/mutator';
import type { BodyType } from '../../../transport/mutator';


type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];


  /**
 * @summary Server-side forwarding destinations of the store
 */
export const getAdminV1StoresStoreIdAnalyticsDestinations = (
    { storeId }: GetAdminV1StoresStoreIdAnalyticsDestinationsPathParameters,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAnalyticsDestinations200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAnalyticsDestinations200>(
      {url: `/admin/v1/stores/${storeId}/analytics/destinations`, method: 'GET'
    },
      options);
    }
  /**
 * One destination per provider and store. The credential (Meta access token, GA4 API secret) is encrypted at rest and never returned; rotate it with PATCH
 * @summary Connect Meta Conversions API or GA4 Measurement Protocol
 */
export const postAdminV1StoresStoreIdAnalyticsDestinations = (
    { storeId }: PostAdminV1StoresStoreIdAnalyticsDestinationsPathParameters,
    createAnalyticsDestinationRequest: BodyType<CreateAnalyticsDestinationRequest>,
 options?: SecondParameter<typeof customInstance<AnalyticsDestination>>,) => {
      return customInstance<AnalyticsDestination>(
      {url: `/admin/v1/stores/${storeId}/analytics/destinations`, method: 'POST',
      headers: {'Content-Type': 'application/json', },
      data: createAnalyticsDestinationRequest
    },
      options);
    }
  /**
 * Setting `status: active` on a paused destination resets `failureCount` and `lastError`. Destinations are never disabled automatically; `failureCount` and `lastError` are for the operator to act on
 * @summary Change config, rotate the credential or pause a destination
 */
export const patchAdminV1StoresStoreIdAnalyticsDestinationsId = (
    { storeId, id }: PatchAdminV1StoresStoreIdAnalyticsDestinationsIdPathParameters,
    patchAdminV1StoresStoreIdAnalyticsDestinationsIdBody: BodyType<PatchAdminV1StoresStoreIdAnalyticsDestinationsIdBody>,
 options?: SecondParameter<typeof customInstance<AnalyticsDestination>>,) => {
      return customInstance<AnalyticsDestination>(
      {url: `/admin/v1/stores/${storeId}/analytics/destinations/${id}`, method: 'PATCH',
      headers: {'Content-Type': 'application/json', },
      data: patchAdminV1StoresStoreIdAnalyticsDestinationsIdBody
    },
      options);
    }
  /**
 * @summary Remove a destination
 */
export const deleteAdminV1StoresStoreIdAnalyticsDestinationsId = (
    { storeId, id }: DeleteAdminV1StoresStoreIdAnalyticsDestinationsIdPathParameters,
 options?: SecondParameter<typeof customInstance<void>>,) => {
      return customInstance<void>(
      {url: `/admin/v1/stores/${storeId}/analytics/destinations/${id}`, method: 'DELETE'
    },
      options);
    }
  /**
 * Updates `lastSuccessAt` or `lastFailureAt` and `lastError`, never `failureCount`
 * @summary Send a test page_view and report the platform's answer
 */
export const postAdminV1StoresStoreIdAnalyticsDestinationsIdTest = (
    { storeId, id }: PostAdminV1StoresStoreIdAnalyticsDestinationsIdTestPathParameters,
 options?: SecondParameter<typeof customInstance<AnalyticsDestinationTestResult>>,) => {
      return customInstance<AnalyticsDestinationTestResult>(
      {url: `/admin/v1/stores/${storeId}/analytics/destinations/${id}/test`, method: 'POST'
    },
      options);
    }
  /**
 * @summary Recorded events, newest first, with per-destination forwarding status
 */
export const getAdminV1StoresStoreIdAnalyticsEvents = (
    { storeId }: GetAdminV1StoresStoreIdAnalyticsEventsPathParameters,
    params?: GetAdminV1StoresStoreIdAnalyticsEventsParams,
 options?: SecondParameter<typeof customInstance<GetAdminV1StoresStoreIdAnalyticsEvents200>>,) => {
      return customInstance<GetAdminV1StoresStoreIdAnalyticsEvents200>(
      {url: `/admin/v1/stores/${storeId}/analytics/events`, method: 'GET',
        params
    },
      options);
    }
  /**
 * @summary Sessions, events by name and forwarding counts for a date range (UTC days)
 */
export const getAdminV1StoresStoreIdAnalyticsSummary = (
    { storeId }: GetAdminV1StoresStoreIdAnalyticsSummaryPathParameters,
    params: GetAdminV1StoresStoreIdAnalyticsSummaryParams,
 options?: SecondParameter<typeof customInstance<AnalyticsSummary>>,) => {
      return customInstance<AnalyticsSummary>(
      {url: `/admin/v1/stores/${storeId}/analytics/summary`, method: 'GET',
        params
    },
      options);
    }
  export type GetAdminV1StoresStoreIdAnalyticsDestinationsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAnalyticsDestinations>>>
export type PostAdminV1StoresStoreIdAnalyticsDestinationsResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAnalyticsDestinations>>>
export type PatchAdminV1StoresStoreIdAnalyticsDestinationsIdResult = NonNullable<Awaited<ReturnType<typeof patchAdminV1StoresStoreIdAnalyticsDestinationsId>>>
export type DeleteAdminV1StoresStoreIdAnalyticsDestinationsIdResult = NonNullable<Awaited<ReturnType<typeof deleteAdminV1StoresStoreIdAnalyticsDestinationsId>>>
export type PostAdminV1StoresStoreIdAnalyticsDestinationsIdTestResult = NonNullable<Awaited<ReturnType<typeof postAdminV1StoresStoreIdAnalyticsDestinationsIdTest>>>
export type GetAdminV1StoresStoreIdAnalyticsEventsResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAnalyticsEvents>>>
export type GetAdminV1StoresStoreIdAnalyticsSummaryResult = NonNullable<Awaited<ReturnType<typeof getAdminV1StoresStoreIdAnalyticsSummary>>>
